/**
 * Client-side upload orchestration for presigned R2 PUTs.
 *
 * Steps per file:
 *   1. Hash the file in the browser (SubtleCrypto SHA-256) so the server
 *      can verify bytes on finalize.
 *   2. Ask the API for a presigned PUT url + draft document row.
 *   3. PUT the file to R2 directly with progress events (XMLHttpRequest -
 *      fetch still can't report upload progress).
 *   4. POST /documents/:id/finalize with the hash to mark the row complete.
 *
 * Errors at any step leave the draft row in a "pending" state; a janitor
 * job can reap those later, we don't block the user on it.
 */

/**
 * Per-file upload cap, mirroring the server's `MAX_UPLOAD_BYTES` default
 * (`api/src/config.ts`). The actual server cap can override this via env var,
 * but keeping this aligned avoids the failure-on-finalize footgun where the
 * browser dropzone accepts a file the server then rejects with `413`.
 */
export const MAX_UPLOAD_SIZE_BYTES = 100 * 1024 * 1024;

/** Allow-list shared by every Dropzone in the app. PDFs + common image types. */
export const UPLOAD_ACCEPTED_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/tiff',
  'image/webp',
];

export type UploadProgress = {
  phase: 'hashing' | 'presigning' | 'uploading' | 'finalizing' | 'done';
  /** 0..1 for the active phase. */
  fraction: number;
  /** Bytes transferred for the uploading phase. */
  loaded?: number;
  total?: number;
};

export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Run `fn` over `items` with at most `limit` concurrent invocations.
 * Resolves to results in the same order as `items`. Errors propagate.
 */
export async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, idx: number) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]!, i);
    }
  }
  const n = Math.max(1, Math.min(limit, items.length));
  const workers = Array.from({ length: n }, worker);
  await Promise.all(workers);
  return results;
}

/**
 * Hash each row's file (concurrency-limited) and return a NEW array with the
 * computed `sha` attached to each row.
 *
 * The sha MUST be consumed from this return value — not read back off the
 * input rows afterward. Callers keep their rows in React state (immutable
 * updates create fresh objects), so the input objects are never mutated; a
 * post-hash `inputRows.filter(r => r.sha)` always yields `[]`, which silently
 * disabled the entire bulk-ingest upload (FE-B1).
 */
export async function hashRowsWithSha<T extends { walked: { file: File } }>(
  rows: T[],
  hash: (file: File) => Promise<string>,
  opts: {
    limit?: number;
    onHashed?: (row: T, sha: string) => void;
    onError?: (row: T, err: unknown) => void;
  } = {},
): Promise<Array<T & { sha?: string }>> {
  return mapLimit(rows, opts.limit ?? 2, async (row): Promise<T & { sha?: string }> => {
    try {
      const sha = await hash(row.walked.file);
      opts.onHashed?.(row, sha);
      return { ...row, sha };
    } catch (err) {
      opts.onError?.(row, err);
      return { ...row }; // sha omitted (exactOptionalPropertyTypes)
    }
  });
}

export interface PutToR2Args {
  url: string;
  file: File;
  headers: Record<string, string>;
  onProgress?: (loaded: number, total: number) => void;
  signal?: AbortSignal;
}

export function putToR2({ url, file, headers, onProgress, signal }: PutToR2Args): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    for (const [k, v] of Object.entries(headers)) {
      // Content-Length is forbidden by XHR to set - the browser sets it.
      if (k.toLowerCase() === 'content-length') continue;
      xhr.setRequestHeader(k, v);
    }
    if (onProgress) {
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) onProgress(ev.loaded, ev.total);
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`upload_failed_${xhr.status}: ${xhr.responseText?.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error('upload_network_error'));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }
    xhr.send(file);
  });
}
